import * as Sentry from '@sentry/react'
import { lazy, Suspense } from 'react'
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom'

import LoadingScreen from './ui/components/Status/Loading/LoadingScreen'
import AuthProvider from './ui/contexts/AuthContext'
import { QueryStateProvider } from './ui/contexts/QueryStateContext'
import ReleaseNotesProvider from './ui/contexts/ReleaseNotesContext'
import TermProvider from './ui/contexts/TermContext'
import TicketInitialDataContext from './ui/contexts/TicketInitialDataContext'
import { ErrorBoundary } from './ui/errors/ErrorBoundary'
import InitialLoad from './ui/hoc/InitialLoad'
import { useNetworkStatusNotifier } from './ui/hooks/useNetworkStatusNotifier'
import { AdminRoutes } from './ui/routes/admin.routes'
import { AuthRoutes } from './ui/routes/auth.routes'
import BackofficeRoutes from './ui/routes/backoffice.routes'
import { OperationalRoutes } from './ui/routes/operational.routes'
import { ResponsibleRoutes } from './ui/routes/responsible.routes'
import { authlessRouteNames } from './ui/routes/routesNames/authlessRouteNames'
import SharedRoutes from './ui/routes/shared.routes'
import TherapistRoutes from './ui/routes/therapist.routes'

const ResponsibleMedicalRecordFill = lazy(
  () =>
    import(
      './ui/modules/operational/views/ResponsibleMedicalRecord/ResponsibleMedicalRecordFill'
    )
)
const RouteNotFind = lazy(() => import('./ui/pages/RouteNotFind'))

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

export default function App() {
  useNetworkStatusNotifier()

  const therapistRoutes = TherapistRoutes()
  const operationalRoutes = OperationalRoutes()
  const responsibleRoutes = ResponsibleRoutes()
  const adminRoutes = AdminRoutes()
  const backofficeRoutes = BackofficeRoutes()
  const authRoutes = AuthRoutes()
  const sharedRoutes = SharedRoutes()

  return (
    <BrowserRouter>
      <SentryRoutes>
        <Route
          element={
            <Suspense fallback={<LoadingScreen fullScreen />}>
              <QueryStateProvider>
                <Outlet />
              </QueryStateProvider>
            </Suspense>
          }
        >
          <Route
            element={
              <AuthProvider>
                <TermProvider>
                  <ReleaseNotesProvider>
                    <InitialLoad>
                      <TicketInitialDataContext>
                        <Outlet />
                      </TicketInitialDataContext>
                    </InitialLoad>
                  </ReleaseNotesProvider>
                </TermProvider>
              </AuthProvider>
            }
            errorElement={<ErrorBoundary />}
          >
            {therapistRoutes}
            {operationalRoutes}
            {responsibleRoutes}
            {adminRoutes}
            {backofficeRoutes}
            {authRoutes}
            {sharedRoutes}
            <Route path={'*'} element={<RouteNotFind />} />
          </Route>
          <Route
            path={authlessRouteNames.responsibleMedicalRecordFill}
            element={<ResponsibleMedicalRecordFill />}
          />
        </Route>
      </SentryRoutes>
    </BrowserRouter>
  )
}
